import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import styled from "@emotion/styled";
import { Formik } from "formik";
import axios from "axios";
import {
  Grid,
  Dialog,
  DialogContent,
  DialogTitle,
  Alert as MuiAlert,
  Box,
  Paper as MuiPaper,
  Card as MuiCard,
  CardContent,
  CircularProgress,
  Button as MuiButton,
  TextField as MuiTextField,
  Divider,
} from "@mui/material";
import { spacing } from "@mui/system";
import Select from "./SelectBox";
import { getApiErrorMessage, isPolandCountry } from "../utils/taxOfficeUtils";

const Card = styled(MuiCard)(spacing);
const Paper = styled(MuiPaper)(spacing);
const Alert = styled(MuiAlert)(spacing);
const TextField = styled(MuiTextField)(spacing);
const Button = styled(MuiButton)(spacing);

const initialValues = {
  name: "",
  street: "",
  buildingIdentifier: "",
  postalCode: "",
  city: "",
  countryId: "",
  taxOfficeNumber: "",
};

const validationSchema = Yup.object().shape({
  name: Yup.string().required("Required"),
  countryId: Yup.number().required("Required"),
  taxOfficeNumber: Yup.string().when("countryId", {
    is: (countryId) => Number(countryId) === 17,
    then: Yup.string().required("Required").matches(/^\d{4}$/, "Must be 4 digits"),
    otherwise: Yup.string().notRequired(),
  }),
});

const AddTaxOffice = ({ refreshTable }) => {
  const [countryData, setCountryData] = useState([]);
  const [open, setOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [submitInError, setSubmitInError] = useState(false);

  useEffect(() => {
    axios.get("/api/v1/countries").then((response) => setCountryData(response.data));
  }, []);

  const handleSubmit = async (values, { setSubmitting }) => {
    const newTaxOffice = {
      name: values.name,
      street: values.street,
      buildingIdentifier: values.buildingIdentifier,
      postalCode: values.postalCode,
      city: values.city,
      countryId: values.countryId,
      taxOfficeNumber: values.taxOfficeNumber,
    };

    axios
      .post("/api/v1/taxOffices", newTaxOffice)
      .then((response) => {
        if (response.status >= 200 && response.status < 300) {
          refreshTable();
          setErrorMessage("Your data has been submitted successfully!");
          setSubmitInError(false);
          setOpen(false);
        } else {
          setErrorMessage("Error adding new tax office.");
          setSubmitInError(true);
        }
      })
      .catch((error) => {
        setErrorMessage(getApiErrorMessage(error, "Error adding new tax office."));
        setSubmitInError(true);
      })
      .finally(() => {
        setSubmitting(false);
      });
  };

  const handleClose = () => {
    setOpen(false);
    setErrorMessage("");
    setSubmitInError(false);
  };

  return (
    <Card mb={0}>
      <CardContent>
        <Paper mt={0}>
          <div>
            <Button variant="contained" color="primary" onClick={() => setOpen(true)}>
              Add Tax Office
            </Button>
            <Dialog
              open={open}
              onClose={handleClose}
              aria-labelledby="add-tax-office-dialog-title"
              fullWidth
              maxWidth="md"
            >
              <DialogTitle id="add-tax-office-dialog-title">Add Tax Office</DialogTitle>
              <Divider />
              <DialogContent>
                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    errors,
                    handleBlur,
                    handleChange,
                    handleSubmit,
                    isSubmitting,
                    touched,
                    values,
                  }) => {
                    const polandSelected = isPolandCountry(values.countryId, countryData);

                    return (
                      <Card mb={6}>
                        <CardContent>
                          {submitInError ? (
                            <Alert severity="error" my={3}>
                              {errorMessage}
                            </Alert>
                          ) : errorMessage.length > 0 ? (
                            <Alert severity="success" my={3}>
                              Your data has been submitted successfully!
                            </Alert>
                          ) : (
                            ""
                          )}

                          {isSubmitting ? (
                            <Box display="flex" justifyContent="center" my={20} mx={40}>
                              <CircularProgress />
                            </Box>
                          ) : (
                            <form onSubmit={handleSubmit}>
                              <Grid container spacing={6}>
                                <Grid item md={8} xs={12}>
                                  <TextField
                                    name="name"
                                    label="Name"
                                    value={values.name}
                                    error={Boolean(touched.name && errors.name)}
                                    fullWidth
                                    helperText={touched.name && errors.name}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    variant="outlined"
                                    my={2}
                                    inputProps={{ "aria-label": "Tax office name" }}
                                  />
                                </Grid>
                                <Grid item md={4} xs={12}>
                                  <Select
                                    name="countryId"
                                    label="Country"
                                    options={JSON.parse(
                                      JSON.stringify(
                                        JSON.parse(
                                          JSON.stringify(countryData).split('"countryId":').join('"key":')
                                        )
                                      )
                                        .split('"countryCode":')
                                        .join('"value":')
                                    )}
                                    onChange={handleChange}
                                  />
                                </Grid>
                              </Grid>
                              <Grid container spacing={6}>
                                <Grid item md={7} xs={9}>
                                  <TextField
                                    name="street"
                                    label="Street"
                                    value={values.street}
                                    error={Boolean(touched.street && errors.street)}
                                    fullWidth
                                    helperText={touched.street && errors.street}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    variant="outlined"
                                    my={2}
                                    inputProps={{ "aria-label": "Street" }}
                                  />
                                </Grid>
                                <Grid item md={2} xs={3}>
                                  <TextField
                                    name="buildingIdentifier"
                                    label="Building Number"
                                    value={values.buildingIdentifier}
                                    error={Boolean(
                                      touched.buildingIdentifier && errors.buildingIdentifier
                                    )}
                                    fullWidth
                                    helperText={
                                      touched.buildingIdentifier && errors.buildingIdentifier
                                    }
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    variant="outlined"
                                    my={2}
                                    inputProps={{ "aria-label": "Building number" }}
                                  />
                                </Grid>
                                <Grid item md={3} xs={12}>
                                  <TextField
                                    name="postalCode"
                                    label="Postal Code"
                                    value={values.postalCode}
                                    error={Boolean(touched.postalCode && errors.postalCode)}
                                    fullWidth
                                    helperText={touched.postalCode && errors.postalCode}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    variant="outlined"
                                    my={2}
                                    inputProps={{ "aria-label": "Postal code" }}
                                  />
                                </Grid>
                              </Grid>
                              <Grid container spacing={6}>
                                <Grid item md={6} xs={12}>
                                  <TextField
                                    name="city"
                                    label="City"
                                    value={values.city}
                                    error={Boolean(touched.city && errors.city)}
                                    fullWidth
                                    helperText={touched.city && errors.city}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    variant="outlined"
                                    my={2}
                                    inputProps={{ "aria-label": "City" }}
                                  />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                  <TextField
                                    name="taxOfficeNumber"
                                    label={polandSelected ? "Tax Office Number" : "Tax Office Number (optional)"}
                                    value={values.taxOfficeNumber}
                                    error={Boolean(touched.taxOfficeNumber && errors.taxOfficeNumber)}
                                    fullWidth
                                    helperText={touched.taxOfficeNumber && errors.taxOfficeNumber}
                                    onBlur={handleBlur}
                                    onChange={handleChange}
                                    variant="outlined"
                                    my={2}
                                    inputProps={{
                                      "aria-label": "Tax office number",
                                      maxLength: 20,
                                    }}
                                  />
                                </Grid>
                              </Grid>
                              <Button type="submit" variant="contained" color="primary" mt={3} my={2} mr={4}>
                                Submit Tax Office
                              </Button>
                              <Button
                                type="button"
                                variant="contained"
                                color="primary"
                                mt={3}
                                my={2}
                                onClick={handleClose}
                              >
                                Close
                              </Button>
                            </form>
                          )}
                        </CardContent>
                      </Card>
                    );
                  }}
                </Formik>
              </DialogContent>
            </Dialog>
          </div>
        </Paper>
      </CardContent>
    </Card>
  );
};

export default AddTaxOffice;
